import React, { useState, useEffect, useCallback } from 'react';

const API = '';

function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem('admin_token') || '');
  const [passphrase, setPassphrase] = useState('');
  const [loginError, setLoginError] = useState('');
  const [logging, setLogging] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [carts, setCarts] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (endpoint) => {
    const res = await fetch(`${API}/api/admin/${endpoint}`, { headers: { 'x-admin-token': token } });
    if (res.status === 401) {
      setToken('');
      sessionStorage.removeItem('admin_token');
      return null;
    }
    if (!res.ok) throw new Error(`Failed to fetch ${endpoint}`);
    return res.json();
  }, [token]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const s = await fetchData('stats');
      if (s) setStats(s);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [fetchData]);

  useEffect(() => {
    if (token) loadDashboard();
  }, [token, loadDashboard]);

  useEffect(() => {
    document.title = 'Admin Dashboard — VowAuthor';
    return () => { document.title = 'VowAuthor — Beautiful Wedding Vows'; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    setLoginError('');
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passphrase })
      });
      const data = await res.json();
      if (data.success) {
        setToken(data.token);
        sessionStorage.setItem('admin_token', data.token);
        setPassphrase('');
      } else {
        setLoginError(data.error);
      }
    } catch {
      setLoginError('Connection failed. Please try again.');
    }
    setLogging(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/admin/logout`, { method: 'POST', headers: { 'x-admin-token': token, 'Content-Type': 'application/json' } });
    } catch { /* ignore */ }
    setToken('');
    sessionStorage.removeItem('admin_token');
  };

  const loadTab = async (tab) => {
    setActiveTab(tab);
    setLoading(true);
    try {
      if (tab === 'overview') {
        const s = await fetchData('stats');
        if (s) setStats(s);
      } else if (tab === 'purchases') {
        const p = await fetchData('purchases');
        if (p) setPurchases(p);
      } else if (tab === 'carts') {
        const c = await fetchData('abandoned-carts');
        if (c) setCarts(c);
      } else if (tab === 'generations') {
        const g = await fetchData('generations');
        if (g) setGenerations(g);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtMoney = (n) => `$${Number(n).toFixed(2)}`;

  const deleteCart = async (id) => {
    if (!window.confirm('Delete this abandoned cart?')) return;
    try {
      const res = await fetch(`${API}/api/admin/abandoned-carts/${id}`, { method: 'DELETE', headers: { 'x-admin-token': token } });
      if (res.ok) setCarts(prev => prev.filter(c => c.id !== id));
    } catch (e) { console.error(e); }
  };

  const accent = '#1e3a5f';
  const accentBorder = 'rgba(30,58,95,0.3)';

  if (!token) {
    return (
      <div className="container py-5" style={{ position: 'relative', zIndex: 2 }}>
        <div className="row justify-content-center">
          <div className="col-md-5">
            <div style={{ background: 'white', borderRadius: '16px', padding: '40px', boxShadow: '0 4px 24px rgba(30,58,95,0.12)', textAlign: 'center' }}>
              <h2 style={{ color: accent, fontWeight: 700, fontSize: '1.6rem', marginBottom: '1.5rem' }}>Admin Dashboard</h2>
              <form onSubmit={handleLogin}>
                <div className="mb-3">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter passphrase"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    autoFocus
                    style={{ background: '#f7fafc', border: `1px solid ${accentBorder}`, color: '#2d3748', textAlign: 'center', padding: '12px' }}
                  />
                </div>
                {loginError && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{loginError}</div>}
                <button type="submit" disabled={logging || !passphrase} className="btn w-100" style={{ background: `linear-gradient(135deg, ${accent} 0%, #1a365d 100%)`, color: 'white', fontWeight: 600 }}>
                  {logging ? 'Verifying...' : 'Enter'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tabStyle = (t) => ({
    background: activeTab === t ? accent : 'white',
    color: activeTab === t ? 'white' : '#4a5568',
    border: `1px solid ${activeTab === t ? accent : '#e2e8f0'}`,
    borderRadius: '8px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontWeight: activeTab === t ? 600 : 400,
    fontSize: '0.9rem',
    transition: 'all 0.2s'
  });

  const cardStyle = {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center',
    boxShadow: '0 1px 4px rgba(30,58,95,0.06)'
  };

  const bigNum = { fontSize: '2rem', fontWeight: 700, color: accent, marginBottom: '4px' };
  const labelSt = { fontSize: '0.8rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' };

  const tableWrap = {
    overflowX: 'auto',
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(30,58,95,0.06)'
  };

  const thStyle = {
    background: '#f7fafc',
    color: accent,
    borderBottom: '1px solid #e2e8f0',
    padding: '10px 14px',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: '10px 14px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.88rem',
    color: '#2d3748',
    verticalAlign: 'middle'
  };

  const satisfactionEmoji = { happy: '😊', indifferent: '😐', sad: '😞' };

  return (
    <div style={{ background: '#f7fafc', minHeight: '80vh', padding: '24px 0', position: 'relative', zIndex: 2 }}>
    <div className="container-fluid py-0" style={{ maxWidth: '1200px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: accent, fontWeight: 700, fontSize: '1.4rem', margin: 0 }}>VowAuthor Admin</h2>
        <button onClick={handleLogout} className="btn btn-sm" style={{ color: '#718096', border: '1px solid #e2e8f0' }}>Logout</button>
      </div>

      <div className="d-flex gap-2 mb-4 flex-wrap">
        {['overview', 'purchases', 'generations', 'carts'].map(t => (
          <div key={t} style={tabStyle(t)} onClick={() => loadTab(t)}>
            {t === 'overview' ? '📊 Overview' : t === 'purchases' ? '💳 Purchases' : t === 'generations' ? '💕 Vows' : '🛒 Abandoned Carts'}
          </div>
        ))}
      </div>

      {loading && <div className="text-center py-4" style={{ color: '#718096' }}>Loading...</div>}

      {!loading && activeTab === 'overview' && stats && (
        <>
          <div className="row g-3 mb-4">
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.purchases.total}</div>
                <div style={labelSt}>Total Purchases</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{fmtMoney(stats.purchases.revenue)}</div>
                <div style={labelSt}>Total Revenue</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.purchases.last30Days}</div>
                <div style={labelSt}>Last 30 Days</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.visitors?.toLocaleString() || 0}</div>
                <div style={labelSt}>Total Visitors</div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.codes.total}</div>
                <div style={labelSt}>Total Codes</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.codes.redeemed}</div>
                <div style={labelSt}>Codes Used</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.generations?.total || 0}</div>
                <div style={labelSt}>Vows Generated</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={cardStyle}>
                <div style={bigNum}>{stats.abandonedCarts.total}</div>
                <div style={labelSt}>Abandoned Carts</div>
              </div>
            </div>
          </div>

          {Object.keys(stats.satisfaction).length > 0 && (
            <div style={{ ...cardStyle, textAlign: 'left', marginBottom: '1rem' }}>
              <div style={{ ...labelSt, marginBottom: '12px' }}>Satisfaction Ratings</div>
              <div className="d-flex gap-4">
                {Object.entries(stats.satisfaction).map(([key, count]) => (
                  <div key={key}>
                    <span style={{ fontSize: '1.5rem' }}>{satisfactionEmoji[key] || key}</span>
                    <span style={{ color: '#2d3748', marginLeft: '8px', fontWeight: 600 }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!loading && activeTab === 'purchases' && (
        <div style={tableWrap}>
          <table className="table mb-0" style={{ background: 'transparent' }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Codes</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 && (
                <tr><td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: '#718096' }}>No purchases yet</td></tr>
              )}
              {purchases.map(p => (
                <tr key={p.id}>
                  <td style={tdStyle}>#{p.id}</td>
                  <td style={tdStyle}>{p.email}</td>
                  <td style={tdStyle}>{fmtMoney(p.amount)}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(p.purchase_date)}</td>
                  <td style={tdStyle}>
                    {p.codes.map((c, i) => (
                      <span key={i} style={{
                        display: 'inline-block',
                        background: c.redeemed ? '#f0fff4' : '#f7fafc',
                        color: c.redeemed ? '#276749' : '#718096',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        fontSize: '0.75rem',
                        marginRight: '4px',
                        marginBottom: '2px',
                        fontFamily: 'monospace',
                        border: c.redeemed ? '1px solid #c6f6d5' : '1px solid #e2e8f0'
                      }}>
                        {c.code} {c.redeemed ? '✓' : ''}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && activeTab === 'generations' && (
        <div style={tableWrap}>
          <table className="table mb-0" style={{ background: 'transparent' }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Names</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}>Rating</th>
                <th style={thStyle}>Retries</th>
                <th style={thStyle}>Code</th>
              </tr>
            </thead>
            <tbody>
              {generations.length === 0 && (
                <tr><td colSpan="6" style={{ ...tdStyle, textAlign: 'center', color: '#718096' }}>No vows yet</td></tr>
              )}
              {generations.map(g => (
                <tr key={g.id}>
                  <td style={tdStyle}>#{g.id}</td>
                  <td style={tdStyle}>{g.names || '—'}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(g.created_at)}</td>
                  <td style={tdStyle}>{g.satisfaction_rating ? satisfactionEmoji[g.satisfaction_rating] : '—'}</td>
                  <td style={tdStyle}>{g.retry_count || 0}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem' }}>{g.code || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && activeTab === 'carts' && (
        <div style={tableWrap}>
          <table className="table mb-0" style={{ background: 'transparent' }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Recovery Email</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {carts.length === 0 && (
                <tr><td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: '#718096' }}>No abandoned carts</td></tr>
              )}
              {carts.map(c => (
                <tr key={c.id}>
                  <td style={tdStyle}>#{c.id}</td>
                  <td style={tdStyle}>{c.email}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(c.created_at)}</td>
                  <td style={tdStyle}>
                    <span style={{
                      background: c.email_sent ? '#f0fff4' : '#fff5f5',
                      color: c.email_sent ? '#276749' : '#c53030',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      fontSize: '0.8rem',
                      border: c.email_sent ? '1px solid #c6f6d5' : '1px solid #fed7d7'
                    }}>
                      {c.email_sent ? 'Sent' : 'Not sent'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => deleteCart(c.id)} style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7', borderRadius: '4px', padding: '2px 10px', fontSize: '0.8rem', cursor: 'pointer' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </div>
  );
}

export default Admin;
